import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { AuthState } from '../../../auth/store/auth.model';
import { selectUser } from '../../../auth/store/auth.selector';
import { ProfileApiService } from '../../../pages/profile/profile-api.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';

@Component({
  selector: 'app-parent-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './parent-details.component.html',
  styleUrl: './parent-details.component.css'
})
export class ParentDetailsComponent implements OnInit {
  parentForm: FormGroup;
  userId: number | string | null = null;
  guardianId: number | string | null = null;
  hasGuardianRecord = false;
  isLoading = false;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private store: Store<AuthState>,
    private profileApi: ProfileApiService,
    private toasterService: ToasterService
  ) {
    this.parentForm = this.fb.group({
      client_id: [null],
      user_id: [''],
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.lettersAndSpacesOnly(), this.noLeadingTrailingSpaces(), this.notOnlySpaces()]],
      email: ['', [Validators.email, Validators.maxLength(100)]],
      mobile_no: ['', [Validators.required, Validators.pattern('^[0-9]{9}$')]],
      relation_with_client: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.lettersAndSpacesOnly(), this.noLeadingTrailingSpaces(), this.notOnlySpaces()]],
      company: ['', [Validators.maxLength(150), this.companyNameValidator()]],
      designation: ['', [Validators.maxLength(100), this.designationValidator()]],
      location: ['', [Validators.maxLength(100), this.locationValidator()]],
      is_active: [true]
    });
  }

  ngOnInit() {
    this.store.select(selectUser).pipe(take(1)).subscribe({
      next: (user) => {
        const masterUserId = localStorage.getItem('master_user_id') ?? user?.id ?? this.getUserIdFromStorage();
        if (masterUserId) {
          this.userId = masterUserId;
          this.parentForm.patchValue({
            client_id: masterUserId,
            user_id: masterUserId
          });
          this.loadGuardian(masterUserId);
        }
      }
    });
  }

  private loadGuardian(id: number | string) {
    this.isLoading = true;
    this.profileApi.getClientGuardianByClientId(id).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const rawData = res?.data ?? res;
        const data = Array.isArray(rawData) ? rawData[0] : rawData;
        if (!data) {
          this.hasGuardianRecord = false;
          return;
        }

        this.hasGuardianRecord = true;
        if (data.id) {
          this.guardianId = data.id;
        }

        // API may return different keys (user_id / client_id). Ensure client_id is always populated.
        const resolvedClientId = data?.user_id ?? data?.client_id ?? data?.clientId ?? id;

        this.parentForm.patchValue({
          client_id: resolvedClientId ?? "",
          user_id: resolvedClientId ?? '',
          name: data.name ?? '',
          email: data.email ?? '',
          mobile_no: data.mobile_no ?? '',
          relation_with_client: data.relation_with_client ?? '',
          company: data.company ?? '',
          designation: data.designation ?? '',
          location: data.location ?? '',
          is_active: data.is_active ?? true
        });

        if (!this.guardianId && data.id) {
          this.guardianId = data.id;
          localStorage.setItem('guardian_id', data.id.toString());
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        if (err?.status === 404 || err?.error?.message === 'Client guardian not found') {
          this.hasGuardianRecord = false;
          return;
        }
        this.toasterService.setToaster('error', 'Error', 'Unable to load guardian details.');
      }
    });
  }

  saveGuardian() {
    if (this.parentForm.invalid) {
      this.parentForm.markAllAsTouched();
      return;
    }

    const formValue = this.parentForm.value;
    const clientId = formValue.client_id ?? this.userId;

    if (!clientId) {
      this.toasterService.setToaster('error', 'Error', 'Client ID not found');
      return;
    }

    // Backend validates client_id. Ensure it's included for both create and update.
    const payload = {
      client_id: clientId,
      user_id: clientId,
      name: formValue.name,
      email: formValue.email,
      mobile_no: formValue.mobile_no,
      relation_with_client: formValue.relation_with_client,
      company: formValue.company,
      designation: formValue.designation,
      location: formValue.location,
      is_active: formValue.is_active
    };

    this.isSaving = true;
    const request = this.hasGuardianRecord && this.userId
      ? this.profileApi.updateClientGuardianByClientId(this.userId, payload)
      : this.profileApi.createClientGuardian(payload);

    request.subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res?.status === 'success' || res?.status === 200) {
          this.toasterService.setToaster('success', 'Success', res?.message ?? 'Guardian saved successfully.');
          const newGuardianId = res?.data?.id;
          if (newGuardianId !== undefined && newGuardianId !== null) {
            this.guardianId = newGuardianId;
            this.hasGuardianRecord = true;
            localStorage.setItem('guardian_id', newGuardianId.toString());
          }
        } else {
          this.toasterService.setToaster('error', 'Error', res?.message ?? 'Failed to save guardian');
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        this.toasterService.setToaster('error', 'Error', err?.message || 'Unable to save guardian details');
      }
    });
  }

  private collectValidationMessages(formGroup: FormGroup, prefix = ''): string[] {
    const messages: string[] = [];

    Object.keys(formGroup.controls).forEach(controlName => {
      const control = formGroup.controls[controlName];
      const key = prefix ? `${prefix}.${controlName}` : controlName;

      if (control instanceof FormGroup) {
        messages.push(...this.collectValidationMessages(control, key));
      } else if (control.invalid) {
        messages.push(...this.getControlErrorMessages(control, controlName));
      }
    });

    return messages;
  }

  private getControlErrorMessages(control: AbstractControl, controlName: string): string[] {
    const errors = control.errors as ValidationErrors | null;
    const label = this.getLabelForControl(controlName);
    const messages: string[] = [];

    if (!errors) {
      return messages;
    }

    if (errors['required']) {
      messages.push(`${label} is required.`);
    }
    if (errors['email']) {
      messages.push(`${label} must be a valid email.`);
    }
    if (errors['pattern']) {
      if (controlName === 'mobile_no') {
        messages.push(`${label} must contain 10 to 15 digits.`);
      } else {
        messages.push(`${label} has an invalid format.`);
      }
    }
    if (errors['maxlength']) {
      messages.push(`${label} cannot exceed ${errors['maxlength'].requiredLength} characters.`);
    }
    if (errors['minlength']) {
      messages.push(`${label} must be at least ${errors['minlength'].requiredLength} characters.`);
    }

    return messages;
  }

  private getLabelForControl(controlName: string): string {
    const labels: Record<string, string> = {
      client_id: 'Client ID',
      user_id: 'User ID',
      name: 'Name',
      email: 'Email',
      mobile_no: 'Mobile number',
      relation_with_client: 'Relation with client',
      company: 'Company',
      designation: 'Designation',
      location: 'Location',
      is_active: 'Active status'
    };
    return labels[controlName] || controlName;
  }

  noLeadingTrailingSpaces() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const hasLeadingSpace = value.charAt(0) === ' ';
        const hasTrailingSpace = value.charAt(value.length - 1) === ' ';
        if (hasLeadingSpace || hasTrailingSpace) {
          return { leadingTrailingSpaces: true };
        }
      }
      return null;
    };
  }

  notOnlySpaces() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.trim().length === 0 && value.length > 0) {
        return { onlySpaces: true };
      }
      return null;
    };
  }

  lettersAndSpacesOnly() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const lettersAndSpacesRegex = /^[a-zA-Z\s]+$/;
        if (!lettersAndSpacesRegex.test(value)) {
          return { lettersAndSpacesOnly: true };
        }
      }
      return null;
    };
  }

  companyNameValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const companyRegex = /^[a-zA-Z0-9\s.,&()-]+$/;
        if (!companyRegex.test(value)) {
          return { invalidCompanyName: true };
        }
      }
      return null;
    };
  }

  designationValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const designationRegex = /^[a-zA-Z0-9\s.&-]+$/;
        if (!designationRegex.test(value)) {
          return { invalidDesignation: true };
        }
      }
      return null;
    };
  }

  locationValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const locationRegex = /^[a-zA-Z0-9\s,-]+$/;
        if (!locationRegex.test(value)) {
          return { invalidLocation: true };
        }
      }
      return null;
    };
  }

  getFieldError(control: AbstractControl | null, fieldName: string): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const label = this.getLabelForControl(fieldName);
    const errors = control.errors;

    if (errors['required']) {
      return `${label} is required.`;
    }
    if (errors['minlength']) {
      return `${label} must be between ${errors['minlength'].requiredLength} and ${errors['maxlength']?.requiredLength || ''} characters.`;
    }
    if (errors['maxlength']) {
      return `${label} must not exceed ${errors['maxlength'].requiredLength} characters.`;
    }
    if (errors['leadingTrailingSpaces']) {
      return `${label} must not have leading or trailing spaces.`;
    }
    if (errors['onlySpaces']) {
      return `${label} cannot contain only spaces.`;
    }
    if (errors['lettersAndSpacesOnly']) {
      return `${label} must contain alphabets and spaces only.`;
    }
    if (errors['email']) {
      return 'Please enter a valid email address.';
    }
    if (errors['pattern']) {
      if (fieldName === 'mobile_no') {
        return 'Mobile number must be exactly 10 digits.';
      }
      return `Please enter a valid ${label.toLowerCase()}.`;
    }
    if (errors['invalidCompanyName']) {
      return `${label} contains invalid characters. Only letters, numbers, spaces, ., ,, &, -, (, ) are allowed.`;
    }
    if (errors['invalidDesignation']) {
      return `${label} contains invalid characters. Only letters, numbers, spaces, ., &, - are allowed.`;
    }
    if (errors['invalidLocation']) {
      return `${label} contains invalid characters. Only letters, numbers, spaces, comma, and hyphen are allowed.`;
    }

    return '';
  }

  private getUserIdFromStorage(): number | null {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id ?? null;
    } catch {
      return null;
    }
  }

  private getGuardianIdFromStorage(): number | null {
    const value = localStorage.getItem('guardian_id');
    return value ? Number(value) : null;
  }
}

